import React, { useState, useEffect, useCallback } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import Select from 'react-select';
import axios from 'axios';
import { toast } from 'react-toastify';
import { PuffLoader } from 'react-spinners';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../context/AuthContext';
import useInactivityHandler from '../utils/InactivityHandler';
import API_ENDPOINTS from '../config/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Analytics = () => {
  const { token } = useAuth();
  const [selectedRange, setSelectedRange] = useState('7 days');
  const [transactionData, setTransactionData] = useState({ deposits: 0, withdrawals: 0 });
  const [loading, setLoading] = useState(false);
  useInactivityHandler();

  const options = [
    { value: '1 day', label: '1 day' },
    { value: '7 days', label: '7 days' },
    { value: '2 weeks', label: '2 weeks' },
    { value: '3 months', label: '3 months' },
    { value: '6 months', label: '6 months' },
    { value: '1 year', label: '1 year' },
  ];

  const fetchTransactionSums = useCallback(async (range) => {
    setLoading(true);
    try {
      const response = await axios.post(API_ENDPOINTS.GET_TRANSACTION_SUMS, {
        token,
        range,
      });
      setTransactionData({
        deposits: response.data.totalDeposits || 0,
        withdrawals: Math.abs(response.data.totalWithdrawals || 0),
      });
    } catch (error) {
      console.error('Error fetching transaction data:', error);
      toast.error('Failed to load transaction data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchTransactionSums(selectedRange);
    } else {
      console.error('Token is missing');
      toast.error('Invalid session. Please log in again.');
    }
  }, [selectedRange, token, fetchTransactionSums]);

  const handleRangeChange = (selectedOption) => {
    setSelectedRange(selectedOption.value);
  };

  const data = {
    labels: ['Deposits', 'Withdrawals'],
    datasets: [
      {
        label: 'Amount (USD)',
        data: [transactionData.deposits, transactionData.withdrawals],
        backgroundColor: ['#4CAF50', '#FF5252'],
      },
    ],
  };

  return (
    <div className="flex flex-col items-center p-6 bg-gray-100 min-h-screen">
      <h2 className="text-2xl font-bold mb-4">Analytics</h2>
      <div className="w-64 mb-4">
        <Select
          options={options}
          defaultValue={{ value: '7 days', label: '7 days' }}
          onChange={handleRangeChange}
        />
      </div>

      {loading ? (
        <div className="flex justify-center items-center w-full h-full">
          <PuffLoader color="#36D7B7" size={60} />
        </div>
      ) : (
        <div className="w-full md:w-3/4 lg:w-1/2">
          <Bar data={data} options={{ responsive: true, maintainAspectRatio: false }} />
        </div>
      )}
    </div>
  );
};

export default Analytics;
